import { useCallback, useEffect, useState } from 'react';
import type { ReceiptRow } from './useAllocationTableData';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

export interface InvoiceAllocationApiRow {
  so_ct: string;
  ngay_ct: string;
  ma_kh: string;
  ma_nt: string;
  ty_gia: string;
  t_tt_nt: string;
  da_pb_nt: string;
  da_pb: string;
  da_pb_ct_nt: string;
  da_pb_ct: string;
  tt_nt: string;
  tt: string;
  cl_nt: string;
  tt_yn: number;
  ma_tt: string;
  han_tt: string;
  ngay_phai_tt: string;
  unit_id: string | null;
  ma_ct: string;
  stt: number;
  document_type: string;
  document_uuid: string;
}

export interface InvoiceAllocationRow extends InvoiceAllocationApiRow {
  uuid: string;
  selected: boolean;
}

export const mapInvoiceAllocationRow = (row: InvoiceAllocationApiRow): InvoiceAllocationRow => ({
  ...row,
  uuid: row.document_uuid,
  selected: false
});

export const useInvoiceAllocationDetails = (receipt: ReceiptRow | null) => {
  const { entity } = useAuth();
  const [rows, setRows] = useState<InvoiceAllocationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(value => value + 1), []);
  const toggleSelection = useCallback((uuid: string, selected: boolean) => {
    setRows(currentRows => currentRows.map(row => (row.uuid === uuid ? { ...row, selected } : row)));
  }, []);
  const documentType = receipt?.document_type?.trim();
  const documentUuid = receipt?.document_uuid?.trim();

  useEffect(() => {
    const controller = new AbortController();

    if (!entity?.slug || !documentType || !documentUuid) {
      setRows([]);
      setIsLoading(false);
      return () => controller.abort();
    }

    setRows([]);
    setIsLoading(true);

    void api
      .get<InvoiceAllocationApiRow[]>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHAN_BO_THU_TIEN_CHO_CAC_HOA_DON}/hoa-don/`,
        {
          params: {
            document_type: documentType,
            document_uuid: documentUuid
          },
          signal: controller.signal
        }
      )
      .then(response => {
        if (!controller.signal.aborted) setRows(response.data.map(mapInvoiceAllocationRow));
      })
      .catch((error: { name?: string; code?: string }) => {
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;

        setRows([]);
        toast({
          description: 'Không thể tải chi tiết hóa đơn. Vui lòng thử lại.',
          variant: 'destructive'
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [documentType, documentUuid, entity?.slug, refreshKey]);

  return { rows, isLoading, refresh, toggleSelection };
};
